// DEPRECATED: Scheduled for deletion in my-investments-admin admin-epic-6.
// Do NOT add new features here — they belong in the admin console.
// See https://github.com/maletsiri/my-investments-admin/blob/main/docs/architecture.md §6.
// Gated on ≥ 4 weeks PROD dual-run with ≥ 95% usage through admin console.
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  Crown,
  DollarSign,
  TrendingUp,
  UserCheck,
  UserX,
  Search,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Shield,
  AlertCircle,
  Gift,
  BarChart3,
  CreditCard,
  Calendar,
  Download,
  Lock,
  ClipboardList,
} from 'lucide-react';
import { usePermissionContext } from '../contexts/PermissionContext';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8001';

type AdminTab = 'overview' | 'revenue' | 'subscriptions' | 'payments' | 'audit-log';

interface DashboardStats {
  total_users: number;
  pro_users: number;
  free_users: number;
  new_users_today: number;
  new_users_this_week: number;
  new_users_this_month: number;
  total_revenue: number;
  revenue_this_month: number;
  active_subscriptions: number;
  churned_subscriptions: number;
}

interface UserSummary {
  id: number;
  email: string;
  full_name: string | null;
  subscription_tier: string;
  is_active: boolean;
  is_email_verified: boolean;
  subscription_expires_at: string | null;
  created_at: string;
  ai_requests_this_month: number;
}

interface RevenueByPeriod {
  period: string;
  amount: number;
  currency: string;
  count: number;
}

interface SubscriptionRecord {
  id: number;
  user_id: number;
  provider: string;
  plan: string;
  amount: number;
  currency: string;
  status: string;
  starts_at: string;
  expires_at: string;
  cancelled_at: string | null;
  created_at: string;
}

interface PaymentRecord {
  id: number;
  user_id: number;
  provider: string;
  amount: number;
  currency: string;
  status: string;
  payment_method: string | null;
  description: string | null;
  created_at: string;
}

interface CohortRow {
  signup_month: string;
  cohort_size: number;
  retention: Record<string, number>;
}

interface RevenueReport {
  mrr: number;
  total_subscribers: number;
  new_subscribers_this_month: number;
  churned_this_month: number;
  churn_rate_percent: number;
  cohort_data: CohortRow[];
}

interface AuditLogEntry {
  id: number;
  admin_user_id: number;
  admin_name: string | null;
  action: string;
  target_type: string;
  target_id: number | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

const AUDIT_ACTION_LABELS: Record<string, string> = {
  refund_processed: 'Refund Processed',
  trial_extended: 'Trial Extended',
  ticket_replied: 'Ticket Reply',
  ticket_status_changed: 'Ticket Status Changed',
  announcement_created: 'Announcement Created',
  announcement_deactivated: 'Announcement Deactivated',
  user_account_modified: 'User Modified',
};

export default function Admin() {
  const navigate = useNavigate();
  const { hasPermission } = usePermissionContext();
  const [activeTab, setActiveTab] = useState<AdminTab>('overview');
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [users, setUsers] = useState<UserSummary[]>([]);
  const [revenueData, setRevenueData] = useState<RevenueByPeriod[]>([]);
  const [subscriptions, setSubscriptions] = useState<SubscriptionRecord[]>([]);
  const [payments, setPayments] = useState<PaymentRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [tierFilter, setTierFilter] = useState<string>('');
  const [page, setPage] = useState(0);
  const [selectedUser, setSelectedUser] = useState<UserSummary | null>(null);
  const [grantDays, setGrantDays] = useState(30);
  const [actionLoading, setActionLoading] = useState(false);
  const [revenuePeriod, setRevenuePeriod] = useState<'daily' | 'weekly' | 'monthly'>('monthly');
  const [revenueReport, setRevenueReport] = useState<RevenueReport | null>(null);
  const [subStatusFilter, setSubStatusFilter] = useState<string>('');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState<string>('');
  const [auditLog, setAuditLog] = useState<AuditLogEntry[]>([]);
  const [auditLogTotal, setAuditLogTotal] = useState(0);
  const [auditLogPage, setAuditLogPage] = useState(1);
  const [auditActionFilter, setAuditActionFilter] = useState('');
  const [auditAdminFilter, setAuditAdminFilter] = useState('');
  const [expandedAuditRow, setExpandedAuditRow] = useState<number | null>(null);

  const pageSize = 20;

  useEffect(() => {
    loadData();
  }, [page, search, tierFilter]);

  useEffect(() => {
    if (activeTab === 'revenue') {
      loadRevenueData();
    } else if (activeTab === 'subscriptions') {
      loadSubscriptions();
    } else if (activeTab === 'payments') {
      loadPayments();
    } else if (activeTab === 'audit-log') {
      loadAuditLog();
    }
  }, [activeTab, revenuePeriod, subStatusFilter, paymentStatusFilter, auditLogPage, auditActionFilter, auditAdminFilter]);

  const getToken = () => localStorage.getItem('token');

  const apiRequest = async (endpoint: string, options: RequestInit = {}) => {
    const token = getToken();
    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...options.headers,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Request failed' }));
      throw new Error(error.detail || 'Request failed');
    }

    return response.json();
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [statsData, usersData] = await Promise.all([
        apiRequest('/api/v1/admin/stats'),
        apiRequest(
          `/api/v1/admin/users?skip=${page * pageSize}&limit=${pageSize}${
            search ? `&search=${encodeURIComponent(search)}` : ''
          }${tierFilter ? `&tier=${tierFilter}` : ''}`
        ),
      ]);
      setStats(statsData);
      setUsers(usersData);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const loadRevenueData = async () => {
    try {
      const [data, report] = await Promise.all([
        apiRequest(`/api/v1/admin/revenue?period=${revenuePeriod}`),
        apiRequest('/api/v1/admin/revenue-report'),
      ]);
      setRevenueData(data);
      setRevenueReport(report);
    } catch (err) {
      console.error('Failed to load revenue data:', err);
    }
  };

  const loadSubscriptions = async () => {
    try {
      const endpoint = `/api/v1/admin/subscriptions?limit=100${subStatusFilter ? `&status_filter=${subStatusFilter}` : ''}`;
      const data = await apiRequest(endpoint);
      setSubscriptions(data);
    } catch (err) {
      console.error('Failed to load subscriptions:', err);
    }
  };

  const loadPayments = async () => {
    try {
      const endpoint = `/api/v1/admin/payments?limit=100${paymentStatusFilter ? `&status_filter=${paymentStatusFilter}` : ''}`;
      const data = await apiRequest(endpoint);
      setPayments(data);
    } catch (err) {
      console.error('Failed to load payments:', err);
    }
  };

  const loadAuditLog = async () => {
    try {
      const params = new URLSearchParams({ page: String(auditLogPage), limit: '50' });
      if (auditActionFilter) params.append('action', auditActionFilter);
      if (auditAdminFilter) params.append('admin_id', auditAdminFilter);
      const data = await apiRequest(`/api/v1/admin/audit-log?${params}`);
      setAuditLog(data.items);
      setAuditLogTotal(data.total);
    } catch (err) {
      console.error('Failed to load audit log:', err);
    }
  };

  const exportRevenueCSV = () => {
    const headers = ['Period', 'Amount', 'Currency', 'Transaction Count'];
    const rows = revenueData.map((r) => [r.period, r.amount.toFixed(2), r.currency, r.count]);
    const csv = [headers, ...rows].map((row) => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `revenue-report-${revenuePeriod}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const toggleUserActive = async (userId: number) => {
    setActionLoading(true);
    try {
      await apiRequest(`/api/v1/admin/users/${userId}/toggle-active`, { method: 'PATCH' });
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to toggle user status');
    } finally {
      setActionLoading(false);
    }
  };

  const grantPro = async (userId: number) => {
    setActionLoading(true);
    try {
      await apiRequest(`/api/v1/admin/users/${userId}/grant-pro?days=${grantDays}`, {
        method: 'PATCH',
      });
      await loadData();
      setSelectedUser(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to grant Pro subscription');
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatCurrency = (amount: number) => {
    return `R${amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
  };

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
        <p className="text-red-600 dark:text-red-400 text-lg">{error}</p>
        <p className="text-gray-500 mt-2">Make sure you have admin privileges.</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Shield className="h-7 w-7 text-purple-500" />
            Admin Dashboard
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Manage users, subscriptions, and monitor platform metrics
          </p>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
        {[
          { id: 'overview' as AdminTab, icon: Users, label: 'Overview' },
          { id: 'revenue' as AdminTab, icon: BarChart3, label: 'Revenue Reports' },
          { id: 'subscriptions' as AdminTab, icon: Crown, label: 'Subscriptions' },
          { id: 'payments' as AdminTab, icon: CreditCard, label: 'Payments' },
          { id: 'audit-log' as AdminTab, icon: ClipboardList, label: 'Audit Log' },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Access Control quick links (shown on all tabs if permitted) */}
      {(hasPermission('admin:user-manage') || hasPermission('admin:audit-read')) && (
        <div className="flex gap-3">
          {hasPermission('admin:user-manage') && (
            <button
              onClick={() => navigate('/admin/access-control/users')}
              className="flex items-center gap-2 px-4 py-2.5 bg-indigo-900/30 border border-indigo-800/50 rounded-lg text-indigo-300 hover:bg-indigo-900/50 transition-colors text-sm"
            >
              <Lock className="h-4 w-4" />
              Access Control — Users
            </button>
          )}
          {hasPermission('admin:audit-read') && (
            <button
              onClick={() => navigate('/admin/access-control/audit')}
              className="flex items-center gap-2 px-4 py-2.5 bg-teal-900/30 border border-teal-800/50 rounded-lg text-teal-300 hover:bg-teal-900/50 transition-colors text-sm"
            >
              <ClipboardList className="h-4 w-4" />
              RBAC Audit Log
            </button>
          )}
        </div>
      )}

      {/* Overview Tab Content */}
      {activeTab === 'overview' && (
        <>
          {/* Stats Grid */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-md">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <Users className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {stats.total_users}
                </p>
                <p className="text-sm text-gray-500">Total Users</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-md">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
                <Crown className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {stats.pro_users}
                </p>
                <p className="text-sm text-gray-500">Pro Users</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-md">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                <DollarSign className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {formatCurrency(stats.revenue_this_month)}
                </p>
                <p className="text-sm text-gray-500">Revenue (Month)</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-md">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                <TrendingUp className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  +{stats.new_users_this_week}
                </p>
                <p className="text-sm text-gray-500">New (Week)</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quick Stats Row */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3 text-center">
            <p className="text-lg font-semibold text-gray-900 dark:text-white">
              {stats.new_users_today}
            </p>
            <p className="text-xs text-gray-500">New Today</p>
          </div>
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3 text-center">
            <p className="text-lg font-semibold text-gray-900 dark:text-white">
              {stats.new_users_this_month}
            </p>
            <p className="text-xs text-gray-500">New This Month</p>
          </div>
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3 text-center">
            <p className="text-lg font-semibold text-gray-900 dark:text-white">
              {stats.active_subscriptions}
            </p>
            <p className="text-xs text-gray-500">Active Subs</p>
          </div>
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3 text-center">
            <p className="text-lg font-semibold text-gray-900 dark:text-white">
              {stats.churned_subscriptions}
            </p>
            <p className="text-xs text-gray-500">Churned (Month)</p>
          </div>
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3 text-center">
            <p className="text-lg font-semibold text-gray-900 dark:text-white">
              {formatCurrency(stats.total_revenue)}
            </p>
            <p className="text-xs text-gray-500">Total Revenue</p>
          </div>
        </div>
      )}

      {/* Users Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex flex-col md:flex-row gap-4 justify-between">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Users</h2>
            <div className="flex gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(0);
                  }}
                  placeholder="Search email or name..."
                  className="pl-9 pr-4 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm w-64"
                />
              </div>
              <select
                value={tierFilter}
                onChange={(e) => {
                  setTierFilter(e.target.value);
                  setPage(0);
                }}
                className="px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
              >
                <option value="">All Tiers</option>
                <option value="free">Free</option>
                <option value="pro">Pro</option>
              </select>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700/50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  User
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Tier
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Expires
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  AI Used
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Joined
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {users.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                  <td className="px-4 py-3">
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white text-sm">
                        {user.full_name || 'No name'}
                      </p>
                      <p className="text-xs text-gray-500">{user.email}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                        user.subscription_tier === 'pro'
                          ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                          : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                      }`}
                    >
                      {user.subscription_tier === 'pro' && <Crown className="h-3 w-3" />}
                      {user.subscription_tier}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                        user.is_active
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                          : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                      }`}
                    >
                      {user.is_active ? (
                        <UserCheck className="h-3 w-3" />
                      ) : (
                        <UserX className="h-3 w-3" />
                      )}
                      {user.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                    {formatDate(user.subscription_expires_at)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                    {user.ai_requests_this_month}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                    {formatDate(user.created_at)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => toggleUserActive(user.id)}
                        disabled={actionLoading}
                        className="p-1 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                        title={user.is_active ? 'Deactivate' : 'Activate'}
                      >
                        {user.is_active ? (
                          <UserX className="h-4 w-4" />
                        ) : (
                          <UserCheck className="h-4 w-4" />
                        )}
                      </button>
                      <button
                        onClick={() => setSelectedUser(user)}
                        className="p-1 text-yellow-500 hover:text-yellow-600"
                        title="Grant Pro"
                      >
                        <Gift className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Showing {page * pageSize + 1} - {page * pageSize + users.length}
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(Math.max(0, page - 1))}
              disabled={page === 0}
              className="p-2 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              onClick={() => setPage(page + 1)}
              disabled={users.length < pageSize}
              className="p-2 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
        </>
      )}

      {/* Revenue Tab Content */}
      {activeTab === 'revenue' && (
        <div className="space-y-6">
          {/* Revenue Summary Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {stats && (
              <>
                <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-md">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                      <DollarSign className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">
                        {formatCurrency(stats.total_revenue)}
                      </p>
                      <p className="text-sm text-gray-500">Total Revenue (All Time)</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-md">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                      <Calendar className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">
                        {formatCurrency(stats.revenue_this_month)}
                      </p>
                      <p className="text-sm text-gray-500">Revenue This Month</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-md">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                      <TrendingUp className="h-5 w-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">
                        {formatCurrency(revenueReport?.mrr ?? stats.active_subscriptions * 99)}
                      </p>
                      <p className="text-sm text-gray-500">MRR (This Month)</p>
                    </div>
                  </div>
                </div>
              </>
            )}
            {revenueReport && (
              <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-md">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
                    <UserX className="h-5 w-5 text-red-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">
                      {revenueReport.churn_rate_percent}%
                    </p>
                    <p className="text-sm text-gray-500">Monthly Churn</p>
                    <p className="text-xs text-gray-400">{revenueReport.churned_this_month} users churned</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Revenue Chart */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-green-500" />
                  Revenue Over Time
                </h2>
                <div className="flex items-center gap-3">
                  <select
                    value={revenuePeriod}
                    onChange={(e) => setRevenuePeriod(e.target.value as 'daily' | 'weekly' | 'monthly')}
                    className="px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
                  >
                    <option value="daily">Daily (30 days)</option>
                    <option value="weekly">Weekly (12 weeks)</option>
                    <option value="monthly">Monthly (12 months)</option>
                  </select>
                  <button
                    onClick={exportRevenueCSV}
                    className="flex items-center gap-2 px-3 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg text-sm"
                  >
                    <Download className="h-4 w-4" />
                    Export CSV
                  </button>
                </div>
              </div>
            </div>

            {/* Simple bar chart visualization */}
            <div className="p-4">
              {revenueData.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <BarChart3 className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No revenue data available</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {revenueData.map((item) => {
                    const maxAmount = Math.max(...revenueData.map((r) => r.amount), 1);
                    const widthPercent = (item.amount / maxAmount) * 100;
                    return (
                      <div key={item.period} className="flex items-center gap-4">
                        <span className="w-24 text-sm text-gray-600 dark:text-gray-400 font-mono">
                          {item.period}
                        </span>
                        <div className="flex-1 bg-gray-100 dark:bg-gray-700 rounded-full h-8 overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-end pr-2"
                            style={{ width: `${Math.max(widthPercent, 5)}%` }}
                          >
                            <span className="text-xs font-medium text-white">
                              {formatCurrency(item.amount)}
                            </span>
                          </div>
                        </div>
                        <span className="w-16 text-right text-xs text-gray-500">
                          {item.count} txns
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Revenue Summary */}
            {revenueData.length > 0 && (
              <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-sm text-gray-500">Total (Period)</p>
                    <p className="text-lg font-bold text-gray-900 dark:text-white">
                      {formatCurrency(revenueData.reduce((sum, r) => sum + r.amount, 0))}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Transactions</p>
                    <p className="text-lg font-bold text-gray-900 dark:text-white">
                      {revenueData.reduce((sum, r) => sum + r.count, 0)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Avg per Period</p>
                    <p className="text-lg font-bold text-gray-900 dark:text-white">
                      {formatCurrency(
                        revenueData.reduce((sum, r) => sum + r.amount, 0) / revenueData.length
                      )}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Cohort Retention Table */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Users className="h-5 w-5 text-blue-500" />
                Cohort Retention
              </h2>
            </div>
            <div className="p-4">
              {!revenueReport || revenueReport.cohort_data.length === 0 ? (
                <p className="text-center text-gray-500 py-8">
                  Not enough data yet — cohort analysis requires at least 2 months of subscriber history
                </p>
              ) : (() => {
                const maxMonths = Math.max(
                  ...revenueReport.cohort_data.map((c) => Object.keys(c.retention).length)
                );
                return (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left">
                        <th className="px-2 py-2 text-gray-500 dark:text-gray-400 font-medium">Cohort</th>
                        <th className="px-2 py-2 text-gray-500 dark:text-gray-400 font-medium text-center">Size</th>
                        {Array.from({ length: maxMonths }, (_, i) => (
                            <th key={i} className="px-2 py-2 text-gray-500 dark:text-gray-400 font-medium text-center">
                              M+{i}
                            </th>
                          )
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {revenueReport.cohort_data.map((cohort) => {
                        return (
                          <tr key={cohort.signup_month} className="border-t border-gray-100 dark:border-gray-700">
                            <td className="px-2 py-2 font-mono text-gray-700 dark:text-gray-300">
                              {cohort.signup_month}
                            </td>
                            <td className="px-2 py-2 text-center text-gray-600 dark:text-gray-400">
                              {cohort.cohort_size}
                            </td>
                            {Array.from({ length: maxMonths }, (_, i) => {
                              const val = cohort.retention[String(i)];
                              const pct = val !== undefined ? val : null;
                              const bg =
                                pct === null
                                  ? ''
                                  : pct >= 80
                                  ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                                  : pct >= 50
                                  ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400'
                                  : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400';
                              return (
                                <td key={i} className={`px-2 py-2 text-center rounded ${bg}`}>
                                  {pct !== null ? `${pct}%` : '—'}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Subscriptions Tab Content */}
      {activeTab === 'subscriptions' && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Crown className="h-5 w-5 text-yellow-500" />
                Subscription History
              </h2>
              <select
                value={subStatusFilter}
                onChange={(e) => setSubStatusFilter(e.target.value)}
                className="px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
              >
                <option value="">All Statuses</option>
                <option value="active">Active</option>
                <option value="cancelled">Cancelled</option>
                <option value="expired">Expired</option>
              </select>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    ID
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    User ID
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Plan
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Amount
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Provider
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Started
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Expires
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {subscriptions.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                      No subscriptions found
                    </td>
                  </tr>
                ) : (
                  subscriptions.map((sub) => (
                    <tr key={sub.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 font-mono">
                        #{sub.id}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                        {sub.user_id}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">
                          <Crown className="h-3 w-3" />
                          {sub.plan}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                        {sub.currency} {sub.amount.toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                        {sub.provider}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span
                          className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                            sub.status === 'active'
                              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                              : sub.status === 'cancelled'
                              ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                              : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                          }`}
                        >
                          {sub.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                        {formatDate(sub.starts_at)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                        {formatDate(sub.expires_at)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Payments Tab Content */}
      {activeTab === 'payments' && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <CreditCard className="h-5 w-5 text-blue-500" />
                Payment History
              </h2>
              <select
                value={paymentStatusFilter}
                onChange={(e) => setPaymentStatusFilter(e.target.value)}
                className="px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
              >
                <option value="">All Statuses</option>
                <option value="success">Success</option>
                <option value="failed">Failed</option>
                <option value="refunded">Refunded</option>
              </select>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    ID
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    User ID
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Amount
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Provider
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Method
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Description
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Date
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {payments.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                      No payments found
                    </td>
                  </tr>
                ) : (
                  payments.map((payment) => (
                    <tr key={payment.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 font-mono">
                        #{payment.id}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                        {payment.user_id}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                        {payment.currency} {payment.amount.toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                        {payment.provider}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                        {payment.payment_method || 'N/A'}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span
                          className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                            payment.status === 'success'
                              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                              : payment.status === 'failed'
                              ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                              : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                          }`}
                        >
                          {payment.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 max-w-xs truncate">
                        {payment.description || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                        {formatDate(payment.created_at)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Audit Log Tab */}
      {activeTab === 'audit-log' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <ClipboardList className="h-5 w-5 text-purple-500" />
              Admin Audit Log
            </h2>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-3 bg-white dark:bg-gray-800 rounded-xl p-4 shadow-md">
            <div className="flex-1 min-w-[180px]">
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                Action
              </label>
              <select
                value={auditActionFilter}
                onChange={(e) => { setAuditActionFilter(e.target.value); setAuditLogPage(1); }}
                className="w-full px-3 py-1.5 text-sm border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              >
                <option value="">All actions</option>
                {Object.entries(AUDIT_ACTION_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>
            <div className="flex-1 min-w-[160px]">
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                Admin ID
              </label>
              <input
                type="number"
                placeholder="Filter by admin ID"
                value={auditAdminFilter}
                onChange={(e) => { setAuditAdminFilter(e.target.value); setAuditLogPage(1); }}
                className="w-full px-3 py-1.5 text-sm border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
            </div>
          </div>

          {/* Table */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
                    <th className="px-4 py-3 text-left font-medium text-gray-500 dark:text-gray-400">Timestamp</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-500 dark:text-gray-400">Admin</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-500 dark:text-gray-400">Action</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-500 dark:text-gray-400">Target</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-500 dark:text-gray-400">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {auditLog.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                        No audit log entries found.
                      </td>
                    </tr>
                  ) : (
                    auditLog.map((entry) => (
                      <React.Fragment key={entry.id}>
                        <tr
                          className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                          onClick={() => setExpandedAuditRow(expandedAuditRow === entry.id ? null : entry.id)}
                        >
                          <td className="px-4 py-3 text-gray-600 dark:text-gray-400 whitespace-nowrap">
                            {new Date(entry.created_at).toLocaleString('en-ZA', {
                              year: 'numeric', month: 'short', day: 'numeric',
                              hour: '2-digit', minute: '2-digit', second: '2-digit',
                            })}
                          </td>
                          <td className="px-4 py-3 text-gray-900 dark:text-white">
                            <div>{entry.admin_name ?? `Admin #${entry.admin_user_id}`}</div>
                            <div className="text-xs text-gray-400">ID {entry.admin_user_id}</div>
                          </td>
                          <td className="px-4 py-3">
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300">
                              {AUDIT_ACTION_LABELS[entry.action] ?? entry.action}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                            <div>{entry.target_type}</div>
                            {entry.target_id !== null && (
                              <div className="text-xs text-gray-400">ID {entry.target_id}</div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-gray-400">
                            <ChevronRight
                              className={`h-4 w-4 transition-transform ${expandedAuditRow === entry.id ? 'rotate-90' : ''}`}
                            />
                          </td>
                        </tr>
                        {expandedAuditRow === entry.id && (
                          <tr key={`${entry.id}-detail`} className="bg-gray-50 dark:bg-gray-900/30">
                            <td colSpan={5} className="px-6 py-3">
                              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Metadata</p>
                              <pre className="text-xs text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 rounded p-3 overflow-x-auto max-h-48">
                                {JSON.stringify(entry.metadata ?? {}, null, 2)}
                              </pre>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {auditLogTotal > 0 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Showing {(auditLogPage - 1) * 50 + 1}–{Math.min(auditLogPage * 50, auditLogTotal)} of {auditLogTotal}
                </p>
                {auditLogTotal > 50 && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => setAuditLogPage((p) => Math.max(1, p - 1))}
                      disabled={auditLogPage === 1}
                      className="p-1.5 rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => setAuditLogPage((p) => p + 1)}
                      disabled={auditLogPage * 50 >= auditLogTotal}
                      className="p-1.5 rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Grant Pro Modal */}
      {selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Grant Pro Subscription
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Grant Pro access to <strong>{selectedUser.email}</strong>
            </p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Duration (days)
              </label>
              <input
                type="number"
                value={grantDays}
                onChange={(e) => setGrantDays(parseInt(e.target.value) || 30)}
                min={1}
                max={365}
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setSelectedUser(null)}
                className="flex-1 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={() => grantPro(selectedUser.id)}
                disabled={actionLoading}
                className="flex-1 py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg flex items-center justify-center gap-2"
              >
                {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Gift className="h-4 w-4" />}
                Grant Pro
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
